import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'; //-tech-debt-react-query;
import { deleteItem, fetchItems, postItem, putItem } from '../support/queries';
import { useFetchItem, useFetchItems } from './base-hooks';
import { CacheKey } from '../types/cache-keys';
import { resolveFileImportData } from '../support/dataImport/fileImportSource';
import { DEFAULT_QUERY_OPTIONS } from '../support/constants';
import {
  Assessment,
  AssessmentQuestion,
  AssessmentSection,
  byOrder,
} from '../support/assessments';

/**
 * Data hooks for the reusable-assessment feature.
 *
 * All five tables are plain generic CRUD in toolbox_db, so reads go through the shared
 * `useFetchItem(s)` helpers and writes through `postItem`/`putItem`/`deleteItem` — `putItem` rather
 * than `patchItem` because toolbox_db's Go runtime serves PUT and 405s on PATCH. That PUT is a
 * *full* update: it validates like a create, so every write must carry the whole record (all
 * required fields) with the change applied on top, not just the changed field, or it 400s.
 *
 * The tree is loaded lazily by level: an assessment's sections are fetched by `?assessment=<id>`,
 * and a section's questions by `?section=<id>`, so a component renders one section's questions
 * without pulling every question in the table. The builder and the fill-in surface share these.
 */

const ENDPOINTS = {
  assessment: 'db/assessment',
  section: 'db/assessment-section',
  question: 'db/assessment-question',
  responseSet: 'db/assessment-response-set',
  response: 'db/assessment-response',
} as const;

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/** Assessments, filtered server-side. Pass `{ is_published: true }` for the fill-in surface. */
export const useFetchAssessments = (params?: Record<string, any>, options?: object) =>
  useFetchItems<Assessment[]>(
    ENDPOINTS.assessment,
    () => [CacheKey.Assessments, JSON.stringify(params ?? {})],
    params,
    options
  );

export const useFetchAssessment = (id?: number | string, options?: object) =>
  useFetchItem<Assessment>(ENDPOINTS.assessment, id, CacheKey.Assessment, undefined, options);

export const useFetchSections = (assessmentId?: number | string, options?: object) =>
  useFetchItems<AssessmentSection[]>(
    ENDPOINTS.section,
    () => [CacheKey.AssessmentSection, assessmentId ?? 'none'],
    assessmentId ? { assessment: assessmentId } : undefined,
    { enabled: !!assessmentId, ...(options || {}) }
  );

export const useFetchQuestions = (sectionId?: number | string, options?: object) =>
  useFetchItems<AssessmentQuestion[]>(
    ENDPOINTS.question,
    () => [CacheKey.AssessmentQuestion, sectionId ?? 'none'],
    sectionId ? { section: sectionId } : undefined,
    { enabled: !!sectionId, ...(options || {}) }
  );

/** Every response set collected against one assessment, for the builder's review tab. */
export const useFetchResponseSets = (assessmentId?: number | string, options?: object) =>
  useFetchItems(
    ENDPOINTS.responseSet,
    () => [CacheKey.AssessmentResponseSet, assessmentId ?? 'none'],
    assessmentId ? { assessment: assessmentId } : undefined,
    { enabled: !!assessmentId, ...(options || {}) }
  );

/** The individual answers within one response set. */
export const useFetchResponses = (responseSetId?: number | string, options?: object) =>
  useFetchItems(
    ENDPOINTS.response,
    () => [CacheKey.AssessmentResponse, responseSetId ?? 'none'],
    responseSetId ? { response_set: responseSetId } : undefined,
    { enabled: !!responseSetId, ...(options || {}) }
  );

/**
 * The raw rows of the `ImportedDataset` behind a `data_import` answer, so the
 * AI-Era report can match the customer's Cisco inventory against the catalog.
 * Reads the dataset's `rows` (no widgetId) via the shared File-Import source.
 * Returns `[]` on any miss so the report degrades gracefully.
 */
export const useInventoryRows = (datasetId?: number) =>
  useQuery({
    queryKey: ['assessment-inventory-rows', datasetId ?? 'none'],
    queryFn: () => resolveFileImportData({ importedDatasetId: datasetId }),
    ...{ enabled: typeof datasetId === 'number', ...DEFAULT_QUERY_OPTIONS },
  });

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

type MutationConfig = {
  endpoint: string;
  /** Keys to invalidate after any write, so both the builder and the portal refresh. */
  invalidate: CacheKey[];
};

const MUTATIONS: Record<string, MutationConfig> = {
  assessment: {
    endpoint: ENDPOINTS.assessment,
    invalidate: [CacheKey.Assessments, CacheKey.Assessment],
  },
  section: {
    endpoint: ENDPOINTS.section,
    invalidate: [CacheKey.AssessmentSection],
  },
  question: {
    endpoint: ENDPOINTS.question,
    invalidate: [CacheKey.AssessmentQuestion],
  },
  responseSet: {
    endpoint: ENDPOINTS.responseSet,
    invalidate: [CacheKey.AssessmentResponseSet],
  },
  response: {
    endpoint: ENDPOINTS.response,
    invalidate: [CacheKey.AssessmentResponse],
  },
};

/**
 * Create / update / delete for one assessment table.
 *
 * Invalidation is by cache-key prefix, so `[AssessmentSection]` refreshes every section list
 * regardless of which assessment id it was keyed under — a section moving assessments (it cannot,
 * but a delete-then-recreate can) still lands.
 */
export const useAssessmentMutations = (model: keyof typeof MUTATIONS) => {
  const queryClient = useQueryClient();
  const config = MUTATIONS[model];

  const onSettled = () => {
    config.invalidate.forEach((key) => queryClient.invalidateQueries({ queryKey: [String(key)] }));
  };

  const create = useMutation({
    mutationFn: (payload: Record<string, any>) => postItem(config.endpoint, payload),
    onSettled,
  });

  const update = useMutation({
    mutationFn: (payload: Record<string, any> & { id: number }) => putItem(config.endpoint, payload),
    onSettled,
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteItem(`${config.endpoint}/${id}`),
    onSettled,
  });

  return {
    create,
    update,
    remove,
    isSaving: create.isPending || update.isPending || remove.isPending,
    error: create.error || update.error || remove.error,
  };
};

/**
 * Persist a new drag order for one level of the tree.
 *
 * Takes the rows in their new visual order and PUTs `order = index` for every row whose position
 * actually changed, then invalidates the list once. Each write carries the whole row with the new
 * `order` on top: the generic runtime treats a PUT as a full update and rejects one missing a
 * required field (a section's key/title, a question's key/text), so sending only `{id, order}`
 * 400s. Used by the builder's drag-and-drop, which is the only way order is set now (the manual
 * "Order" field is gone).
 */
export const useReorder = (model: 'section' | 'question') => {
  const queryClient = useQueryClient();
  const config = MUTATIONS[model];

  return useMutation({
    mutationFn: async (ordered: Array<Record<string, any> & { id: number; order?: number }>) => {
      await Promise.all(
        ordered.map((row, index) =>
          row.order === index
            ? Promise.resolve(null)
            : putItem(config.endpoint, { ...row, order: index })
        )
      );
    },
    onSettled: () =>
      config.invalidate.forEach((key) => queryClient.invalidateQueries({ queryKey: [String(key)] })),
  });
};

// ---------------------------------------------------------------------------
// Clone orchestration
// ---------------------------------------------------------------------------

/** Give a cloned key a unique-ish suffix so the unique constraint on `assessment.key` holds. */
const clonedKey = (key: string): string => {
  const suffix = `copy-${Date.now().toString(36)}`;
  const base = (key || 'assessment').slice(0, 100 - suffix.length - 1);
  return `${base}-${suffix}`;
};

/**
 * Deep-clone an assessment: the assessment row, its sections, and each section's questions.
 *
 * Orchestrated on the client because there is no bespoke clone endpoint — every table is generic
 * CRUD. Sections and questions are created after their parent so the new foreign keys resolve. The
 * copy lands unpublished so a half-built clone is never fillable; the author publishes it when
 * ready. Fetches the source tree itself so a caller only needs the source id.
 */
export const cloneAssessment = async (source: Assessment): Promise<Assessment> => {
  const created: Assessment = await postItem(ENDPOINTS.assessment, {
    key: clonedKey(source.key),
    name: `${source.name} (Copy)`,
    description: source.description,
    purpose: source.purpose,
    subject_type: source.subject_type,
    is_published: false,
    allow_anonymous: source.allow_anonymous,
    is_active: true,
    metadata: source.metadata ?? {},
  });

  const sections: AssessmentSection[] = byOrder(
    (await fetchItems(ENDPOINTS.section, { assessment: source.id })) || []
  );

  for (const section of sections) {
    const newSection: AssessmentSection = await postItem(ENDPOINTS.section, {
      assessment: created.id,
      key: section.key,
      title: section.title,
      description: section.description,
      order: section.order,
      visible_when: section.visible_when,
      active: section.active,
    });

    const questions: AssessmentQuestion[] = byOrder(
      (await fetchItems(ENDPOINTS.question, { section: section.id })) || []
    );

    for (const question of questions) {
      await postItem(ENDPOINTS.question, {
        section: newSection.id,
        key: question.key,
        text: question.text,
        help_text: question.help_text,
        order: question.order,
        response_type: question.response_type,
        response_options: question.response_options,
        visible_when: question.visible_when,
        weight: question.weight,
        required: question.required,
        active: question.active,
      });
    }
  }

  return created;
};
